'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Eye, ChevronLeft } from 'lucide-react';
import MatchChat from './MatchChat';
import MatchScorePanel from './MatchScorePanel';
import MatchResultModal from './MatchResultModal';
import MatchLobby from './MatchLobby';

export type GameType = 'tictactoe' | 'quiz' | 'math';
export type MatchPhase = 'lobby' | 'countdown' | 'playing' | 'result';
export type Cell = 'X' | 'O' | null;

export interface Player {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  rank: 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Diamond' | 'Master';
  country: string;
  winRate: number;
  isOnline: boolean;
  lastSeen?: number;
}

export interface MatchState {
  id: string;
  gameType: GameType;
  phase: MatchPhase;
  round: number;
  maxRounds: number;
  scores: { p1: number; p2: number };
  timeLeft: number;
  totalTime: number;
  moveHistory: MoveRecord[];
  winner: 'p1' | 'p2' | 'draw' | null;
  entryFee: number;
  prizePool: number;
  platformFee: number;
  serverTimestamp: number;
}

export interface MoveRecord {
  player: 'p1' | 'p2';
  move: number | string;
  timestamp: number;
  valid: boolean;
  roundScore?: number;
}

const SELF: Player = {
  id: 'p1',
  name: 'Arjun Kumar',
  avatar: 'AK',
  rating: 1842,
  rank: 'Gold',
  country: '🇮🇳',
  winRate: 68,
  isOnline: true,
};

const OPPONENT: Player = {
  id: 'p2',
  name: 'Marcus Torres',
  avatar: 'MT',
  rating: 1790,
  rank: 'Gold',
  country: '🇺🇸',
  winRate: 61,
  isOnline: true,
};

const RANK_COLORS: Record<string, string> = {
  Bronze: '#CD7F32',
  Silver: '#C0C0C0',
  Gold: '#F59E0B',
  Platinum: '#06B6D4',
  Diamond: '#818CF8',
  Master: '#A855F7',
};

// --- Tic Tac Toe helpers ---
const WINNING_LINES = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
function checkTTTWinner(board: Cell[]): { winner: Cell; line: number[] } | null {
  for (const line of WINNING_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) return { winner: board[a], line };
  }
  return null;
}

// --- Math quiz helpers ---
function generateMathQuestion(): { question: string; answer: number; options: number[] } {
  const ops = ['+', '-', '×'];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let a = Math.floor(Math.random() * 20) + 1;
  let b = Math.floor(Math.random() * 20) + 1;
  let answer: number;
  if (op === '+') answer = a + b;
  else if (op === '-') { if (a < b) [a, b] = [b, a]; answer = a - b; }
  else answer = a * b;
  const wrong = new Set<number>();
  while (wrong.size < 3) {
    const w = answer + (Math.floor(Math.random() * 10) - 5);
    if (w !== answer) wrong.add(w);
  }
  const options = [answer, ...Array.from(wrong)].sort(() => Math.random() - 0.5);
  return { question: `${a} ${op} ${b} = ?`, answer, options };
}

export default function MatchRoomContent() {
  const [matchPhase, setMatchPhase] = useState<MatchPhase>('lobby');
  const [gameType, setGameType] = useState<GameType>('tictactoe');
  const [countdown, setCountdown] = useState(3);
  const [round, setRound] = useState(1);
  const [maxRounds] = useState(3);
  const [scores, setScores] = useState({ p1: 0, p2: 0 });
  const [timeLeft, setTimeLeft] = useState(30);
  const [moveHistory, setMoveHistory] = useState<MoveRecord[]>([]);
  const [winner, setWinner] = useState<'p1' | 'p2' | 'draw' | null>(null);
  const [opponentOnline, setOpponentOnline] = useState(true);
  const [opponentTyping, setOpponentTyping] = useState(false);
  const [spectators] = useState(14);
  const [chatOpen, setChatOpen] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  // TTT state
  const [tttBoard, setTttBoard] = useState<Cell[]>(Array(9).fill(null));
  const [tttTurn, setTttTurn] = useState<'p1' | 'p2'>('p1');
  const [tttWinLine, setTttWinLine] = useState<number[] | null>(null);
  const [tttRoundOver, setTttRoundOver] = useState(false);

  // Math quiz state
  const [mathQ, setMathQ] = useState(() => generateMathQuestion());
  const [mathAnswered, setMathAnswered] = useState(false);
  const [mathSelected, setMathSelected] = useState<number | null>(null);
  const [mathOpponentAnswered, setMathOpponentAnswered] = useState(false);
  const [mathRoundScores, setMathRoundScores] = useState({ p1: 0, p2: 0 });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const opponentRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Simulate opponent presence heartbeat
  useEffect(() => {
    const hb = setInterval(() => {
      setOpponentOnline(true);
    }, 5000);
    return () => clearInterval(hb);
  }, []);

  // Countdown before match
  useEffect(() => {
    if (matchPhase !== 'countdown') return;
    if (countdown <= 0) { setMatchPhase('playing'); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [matchPhase, countdown]);

  // Round timer
  useEffect(() => {
    if (matchPhase !== 'playing') return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current!);
          handleTimeUp();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [matchPhase, round, gameType]);

  // Simulate opponent move for TTT
  useEffect(() => {
    if (matchPhase !== 'playing' || gameType !== 'tictactoe') return;
    if (tttTurn !== 'p2' || tttRoundOver) return;
    const delay = 800 + Math.random() * 1200;
    opponentRef.current = setTimeout(() => {
      const empty = tttBoard.map((c, i) => c === null ? i : -1).filter(i => i >= 0);
      if (empty.length === 0) return;
      const idx = empty[Math.floor(Math.random() * empty.length)];
      handleTTTMove(idx, 'p2');
    }, delay);
    return () => { if (opponentRef.current) clearTimeout(opponentRef.current); };
  }, [tttTurn, tttBoard, matchPhase, gameType, tttRoundOver]);

  // Simulate opponent answering math
  useEffect(() => {
    if (matchPhase !== 'playing' || gameType !== 'math') return;
    if (mathOpponentAnswered) return;
    const delay = 2000 + Math.random() * 4000;
    const t = setTimeout(() => {
      const correct = Math.random() > 0.4;
      setMathOpponentAnswered(true);
      if (correct) setMathRoundScores(s => ({ ...s, p2: s.p2 + Math.floor(Math.random() * 20) + 10 }));
    }, delay);
    return () => clearTimeout(t);
  }, [matchPhase, gameType, mathOpponentAnswered, round]);

  // Simulate opponent typing in chat
  useEffect(() => {
    const t = setInterval(() => {
      if (Math.random() > 0.85) {
        setOpponentTyping(true);
        setTimeout(() => setOpponentTyping(false), 2000);
      }
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const handleTimeUp = useCallback(() => {
    if (gameType === 'tictactoe') {
      endRound(tttTurn === 'p1' ? 'p2' : 'p1');
    } else {
      endRound('draw');
    }
  }, [gameType, tttTurn]);

  const handleTTTMove = useCallback((idx: number, player: 'p1' | 'p2') => {
    if (tttBoard[idx] || tttRoundOver) return;
    const newBoard = [...tttBoard];
    newBoard[idx] = player === 'p1' ? 'X' : 'O';
    setTttBoard(newBoard);

    const record: MoveRecord = { player, move: idx, timestamp: Date.now(), valid: true };
    setMoveHistory(h => [...h, record]);

    const result = checkTTTWinner(newBoard);
    if (result) {
      setTttWinLine(result.line);
      setTttRoundOver(true);
      if (timerRef.current) clearInterval(timerRef.current);
      setTimeout(() => endRound(player), 1200);
    } else if (newBoard.every(c => c !== null)) {
      setTttRoundOver(true);
      if (timerRef.current) clearInterval(timerRef.current);
      setTimeout(() => endRound('draw'), 800);
    } else {
      setTttTurn(player === 'p1' ? 'p2' : 'p1');
    }
  }, [tttBoard, tttRoundOver]);

  const handleMathAnswer = useCallback((opt: number) => {
    if (mathAnswered) return;
    setMathSelected(opt);
    setMathAnswered(true);
    const correct = opt === mathQ.answer;
    const timeBonus = Math.floor(timeLeft * 1.5);
    const pts = correct ? 10 + timeBonus : 0;
    setMathRoundScores(s => ({ ...s, p1: s.p1 + pts }));
    const record: MoveRecord = { player: 'p1', move: String(opt), timestamp: Date.now(), valid: correct, roundScore: pts };
    setMoveHistory(h => [...h, record]);
    if (timerRef.current) clearInterval(timerRef.current);
    setTimeout(() => {
      const p1 = mathRoundScores.p1 + pts;
      const p2 = mathRoundScores.p2;
      endRound(p1 > p2 ? 'p1' : p2 > p1 ? 'p2' : 'draw');
    }, 1500);
  }, [mathAnswered, mathQ, timeLeft, mathRoundScores]);

  const endRound = useCallback((roundWinner: 'p1' | 'p2' | 'draw') => {
    setScores(s => ({
      p1: s.p1 + (roundWinner === 'p1' ? 1 : 0),
      p2: s.p2 + (roundWinner === 'p2' ? 1 : 0),
    }));

    if (round >= maxRounds) {
      setTimeout(() => {
        setMatchPhase('result');
        setShowResult(true);
        // Server-authoritative result recording (simulated)
        setTimeout(() => setResultRecorded(true), 1500);
      }, 800);
    } else {
      setTimeout(() => {
        setRound(r => r + 1);
        setTimeLeft(30);
        setTttBoard(Array(9).fill(null));
        setTttTurn('p1');
        setTttWinLine(null);
        setTttRoundOver(false);
        setMathQ(generateMathQuestion());
        setMathAnswered(false);
        setMathSelected(null);
        setMathOpponentAnswered(false);
        setMathRoundScores({ p1: 0, p2: 0 });
      }, 1500);
    }
  }, [round, maxRounds]);

  const startMatch = (type: GameType) => {
    setGameType(type);
    setMatchPhase('countdown');
    setCountdown(3);
    setScores({ p1: 0, p2: 0 });
    setRound(1);
    setTimeLeft(30);
    setMoveHistory([]);
    setWinner(null);
    setTttBoard(Array(9).fill(null));
    setTttTurn('p1');
    setTttWinLine(null);
    setTttRoundOver(false);
    setMathQ(generateMathQuestion());
    setMathAnswered(false);
    setMathSelected(null);
    setMathOpponentAnswered(false);
    setMathRoundScores({ p1: 0, p2: 0 });
    setShowResult(false);
    setResultRecorded(false);
  };

  const computedWinner = scores.p1 > scores.p2 ? 'p1' : scores.p2 > scores.p1 ? 'p2' : 'draw';

  if (matchPhase === 'lobby') {
    return <MatchLobby self={SELF} opponent={OPPONENT} rankColors={RANK_COLORS} onStart={startMatch} />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: '#0D1424' }}>
        <button
          onClick={() => setMatchPhase('lobby')}
          className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <ChevronLeft size={16} /> Back to Lobby
        </button>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
            <Eye size={12} /> {spectators} watching
          </span>
          <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA' }}>
            Round {round}/{maxRounds}
          </span>
          <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
            🏆 Prize: $9.00
          </span>
        </div>
      </div>

      {/* Countdown overlay */}
      {matchPhase === 'countdown' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(13,20,36,0.95)' }}>
          <div className="text-center">
            <p className="text-lg font-semibold mb-4" style={{ color: 'var(--muted-foreground)' }}>Match starting in</p>
            <div className="text-8xl font-black mb-4" style={{
              background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {countdown === 0 ? 'GO!' : countdown}
            </div>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {gameType === 'tictactoe' ? 'Tic Tac Toe' : 'Math Speed Quiz'} · Best of {maxRounds}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-0" style={{ height: 'calc(100vh - 112px)' }}>
        {/* Main game area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Player presence bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <PlayerPresenceCard player={SELF} side="left" rankColors={RANK_COLORS} score={scores.p1} isActive={gameType === 'tictactoe' ? tttTurn === 'p1' : !mathAnswered} />
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-black" style={{ color: '#F59E0B' }}>{scores.p1}</span>
                <span className="text-lg font-bold" style={{ color: 'var(--muted-foreground)' }}>:</span>
                <span className="text-2xl font-black" style={{ color: '#F59E0B' }}>{scores.p2}</span>
              </div>
              <TimerBadge timeLeft={timeLeft} total={30} />
            </div>
            <PlayerPresenceCard player={OPPONENT} side="right" rankColors={RANK_COLORS} score={scores.p2} isActive={gameType === 'tictactoe' ? tttTurn === 'p2' : mathOpponentAnswered} isOpponent opponentOnline={opponentOnline} />
          </div>

          {/* Game board */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
            {gameType === 'tictactoe' && (
              <TTTBoard
                board={tttBoard}
                winLine={tttWinLine}
                turn={tttTurn}
                roundOver={tttRoundOver}
                onMove={(idx) => { if (tttTurn === 'p1' && !tttRoundOver) handleTTTMove(idx, 'p1'); }}
              />
            )}
            {gameType === 'math' && (
              <MathBoard
                question={mathQ}
                answered={mathAnswered}
                selected={mathSelected}
                opponentAnswered={mathOpponentAnswered}
                onAnswer={handleMathAnswer}
                roundScores={mathRoundScores}
              />
            )}
          </div>

          {/* Move history strip */}
          <MoveHistoryStrip moves={moveHistory} />
        </div>

        {/* Right panel: Score + Chat */}
        <div className="flex flex-col border-l" style={{ width: '300px', borderColor: 'var(--border)', background: '#0D1424', flexShrink: 0 }}>
          <MatchScorePanel scores={scores} round={round} maxRounds={maxRounds} moveHistory={moveHistory} />
          <MatchChat
            self={SELF}
            opponent={OPPONENT}
            opponentTyping={opponentTyping}
            matchPhase={matchPhase}
          />
        </div>
      </div>

      {/* Result modal */}
      {showResult && (
        <MatchResultModal
          winner={computedWinner}
          scores={scores}
          self={SELF}
          opponent={OPPONENT}
          moveHistory={moveHistory}
          resultRecorded={resultRecorded}
          entryFee={5}
          prizePool={9}
          platformFee={1}
          onRematch={() => startMatch(gameType)}
          onExit={() => setMatchPhase('lobby')}
        />
      )}
    </div>
  );
}

// --- Sub-components ---

function PlayerPresenceCard({ player, side, rankColors, score, isActive, isOpponent, opponentOnline }: {
  player: Player; side: 'left' | 'right'; rankColors: Record<string, string>;
  score: number; isActive: boolean; isOpponent?: boolean; opponentOnline?: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 ${side === 'right' ? 'flex-row-reverse' : ''}`}>
      <div className="relative">
        <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
          style={{ background: isOpponent ? 'linear-gradient(135deg, #EF4444, #F59E0B)' : 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
          {player.avatar}
        </div>
        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-900 ${(isOpponent ? opponentOnline : true) ? 'bg-green-400' : 'bg-gray-500'}`} />
        {isActive && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-yellow-400 animate-pulse" />
        )}
      </div>
      <div className={`${side === 'right' ? 'text-right' : ''}`}>
        <p className="text-sm font-bold text-foreground">{player.name}</p>
        <div className={`flex items-center gap-1.5 ${side === 'right' ? 'justify-end' : ''}`}>
          <span className="text-xs font-semibold" style={{ color: rankColors[player.rank] }}>{player.rank}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{player.rating}</span>
          <span className="text-xs">{player.country}</span>
        </div>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{player.winRate}% WR</p>
      </div>
    </div>
  );
}

function TimerBadge({ timeLeft, total }: { timeLeft: number; total: number }) {
  const pct = (timeLeft / total) * 100;
  let color = timeLeft <= 5 ? '#EF4444' : timeLeft <= 10 ? '#F59E0B' : '#10B981';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ background: `${color}22`, border: `1px solid ${color}44` }}>
        <Clock size={12} style={{ color }} />
        <span className="text-sm font-black tabular-nums" style={{ color }}>{timeLeft}s</span>
      </div>
      <div className="w-20 h-1 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function TTTBoard({ board, winLine, turn, roundOver, onMove }: {
  board: Cell[]; winLine: number[] | null; turn: 'p1' | 'p2'; roundOver: boolean; onMove: (i: number) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4">
      {!roundOver && (
        <p className="text-sm font-semibold" style={{ color: 'var(--muted-foreground)' }}>
          {turn === 'p1' ? '🟣 Your turn (X)' : '🟠 Opponent\'s turn (O)'}
        </p>
      )}
      <div className="grid grid-cols-3 gap-2" style={{ width: '240px' }}>
        {board.map((cell, i) => {
          const inWinLine = winLine?.includes(i);
          return (
            <button
              key={i}
              onClick={() => onMove(i)}
              disabled={!!cell || roundOver || turn !== 'p1'}
              className="flex items-center justify-center rounded-xl text-3xl font-black transition-all duration-150"
              style={{
                height: '72px',
                background: inWinLine ? 'rgba(124,58,237,0.3)' : cell ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                border: inWinLine ? '2px solid #7C3AED' : '2px solid rgba(255,255,255,0.08)',
                color: cell === 'X' ? '#A78BFA' : '#F59E0B',
                cursor: !cell && !roundOver && turn === 'p1' ? 'pointer' : 'default',
                transform: inWinLine ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              {cell}
            </button>
          );
        })}
      </div>
      {roundOver && (
        <div className="text-center mt-2">
          {winLine ? (
            <p className="text-sm font-bold" style={{ color: '#10B981' }}>
              {turn === 'p2' ? '🏆 You won this round!' : '😔 Opponent won this round'}
            </p>
          ) : (
            <p className="text-sm font-bold" style={{ color: '#F59E0B' }}>🤝 Round Draw!</p>
          )}
        </div>
      )}
    </div>
  );
}

function MathBoard({ question, answered, selected, opponentAnswered, onAnswer, roundScores }: {
  question: { question: string; answer: number; options: number[] };
  answered: boolean; selected: number | null; opponentAnswered: boolean;
  onAnswer: (n: number) => void; roundScores: { p1: number; p2: number };
}) {
  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-sm">
      <div className="w-full rounded-2xl p-6 text-center" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)' }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--muted-foreground)' }}>Solve Fast</p>
        <p className="text-4xl font-black" style={{ color: 'var(--foreground)' }}>{question.question}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 w-full">
        {question.options.map((opt, i) => {
          const isSelected = selected === opt;
          const isCorrect = opt === question.answer;
          let bg = 'rgba(255,255,255,0.04)';
          let border = 'rgba(255,255,255,0.1)';
          let color = 'var(--foreground)';
          if (answered) {
            if (isCorrect) { bg = 'rgba(16,185,129,0.15)'; border = '#10B981'; color = '#10B981'; }
            else if (isSelected) { bg = 'rgba(239,68,68,0.15)'; border = '#EF4444'; color = '#EF4444'; }
          }
          return (
            <button key={i} onClick={() => onAnswer(opt)} disabled={answered}
              className="py-4 rounded-xl text-xl font-black transition-all duration-150 hover:scale-105"
              style={{ background: bg, border: `2px solid ${border}`, color, cursor: answered ? 'default' : 'pointer' }}>
              {opt}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-6 text-sm">
        <span style={{ color: '#A78BFA' }}>You: <strong>{roundScores.p1} pts</strong></span>
        <span style={{ color: 'var(--muted-foreground)' }}>vs</span>
        <span style={{ color: '#F59E0B' }}>
          Opponent: {opponentAnswered ? <strong>{roundScores.p2} pts</strong> : <span className="animate-pulse">answering…</span>}
        </span>
      </div>
    </div>
  );
}

function MoveHistoryStrip({ moves }: { moves: MoveRecord[] }) {
  const last5 = moves.slice(-5).reverse();
  if (last5.length === 0) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-t overflow-x-auto" style={{ borderColor: 'var(--border)', background: 'rgba(0,0,0,0.2)' }}>
      <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>Moves:</span>
      {last5.map((m, i) => (
        <span key={i} className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-mono"
          style={{
            background: m.valid ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            color: m.valid ? '#10B981' : '#EF4444',
            border: `1px solid ${m.valid ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          }}>
          {m.player === 'p1' ? 'You' : 'Opp'}: {String(m.move)}{m.roundScore !== undefined ? ` (+${m.roundScore})` : ''}
        </span>
      ))}
    </div>
  );
}
